import {
  Bloodwork,
  BloodworkAnalysis,
  PanelMarker,
  PanelRange,
  Client,
  Analysis,
  Section,
} from '../models';
import { CLIENT_LIST_DOSE_TYPE_SECTION } from '../components/bloodworkz/recommended-supplements-panel';
import { MathUtils } from '../utils';

export class AIExportSerializer {
  static replacer(key: string, value: any): any {
    // Handle BloodworkAnalysis
    if (value instanceof BloodworkAnalysis) {
      const {
        bloodworkRanges,
        bloodworkFields,
        panelMarkers, // This is an array generated in init(), but we want the properties directly
        // Practitioner-typed markers. Dropped HERE and re-emitted by
        // AIExportBuilder as `practitionerAddedMarkers`: this generic path
        // strips `field` and `attribute` off every PanelMarker, which is
        // harmless for a shipped marker (its property name says what it is) but
        // left these as a nameless array of numbers — the marker's name
        // survived only inside range.attribute as "custom:leptin".
        customPanelMarkers,
        // The link to the test before this one. It is what gives every marker
        // its previousPanelMarker (and the Results Overview its guarded
        // improved/worsened arrow), but serializing it would nest a SECOND
        // copy of the previous test — and its own previous, recursively —
        // inside every entry, in a file that already carries each test once.
        // The comparison it enables is emitted as resultsOverview
        // changeVsPrevious / comparedOnMarkers instead.
        previousBloodwork,
        previousAnalysis,
        immuneSystemDisorderProbability,
        immuneSystemDisorderProbabilityCategory,
        prostateAnalysis,
        COMMON_IRON_TIBC_SATURATION_FERRITIN,
        possibleRootCauseCategories,

        // MVX Plus. These became real PanelMarkers on 2026-10-08 so the
        // comparison table could see them, and the generic spread below would
        // then carry them into BOTH export callers — including the
        // client-facing "Overall Progress & Trends" draft, which must not see
        // MVX while the section is unreleased and its clinical questions are
        // open with Dr. Schmidt.
        //
        // Dropped here and re-emitted by AIExportBuilder as `mvxInsights`,
        // behind the `includeMvx` option that only the practitioner's raw-data
        // download sets. That block carries the values, the zones and the
        // triage line, so the download loses nothing by this.
        mvx,
        ivx,
        mmx,
        glycA,
        sHdlP,
        citrate,
        valine,
        leucine,
        isoleucine,

        // Remove nested analysis objects that contain root causes
        boneMarrowAnalysis,
        electrolytesAnalysis,
        glucoseAnalysis,
        hormonesAnalysis,
        inflammationOxidativeStressAnalysis,
        ironAnalysis,
        kidneyAnalysis,
        lipidsAnalysis,
        liverBiliaryAnalysis,
        nutrientsAnalysis,
        plateletsAnalysis,
        proteinsAnalysis,
        thyroidAnalysis,
        wbcAnalysis,

        ...rest
      } = value as any;

      // Filter out null/undefined properties that are likely PanelMarkers
      const filtered: any = {};
      Object.keys(rest).forEach(key => {
        const val = rest[key];
        // Keep valid calculations/markers
        if (val && typeof val === 'object' && val instanceof PanelMarker) {
          if (val.hasValue && val.hasValue()) {
            filtered[key] = val;
          }
        } else if (val !== null && val !== undefined) {
          // Keep other non-marker properties (like bloodwork object)
          filtered[key] = val;
        }
      });
      return filtered;
    }

    // Handle Bloodwork
    if (value instanceof Bloodwork) {
      const {
        client,
        collectionDate,
        bloodworkNotes,
        menstrualPhase,
      } = value as any;

      const result: any = {
        client,
        collectionDate,
      };

      // Age replaces the date of birth the Client branch now strips. It has to
      // be computed HERE because this is the only branch that holds both the
      // client and the collection date, and age-at-collection is the clinically
      // correct figure anyway (an old test read against today's age is wrong).
      // Capped at 90: HIPAA's Safe Harbor method treats an age over 89 as an
      // identifier on its own, and nothing in blood work interpretation turns
      // on 90 vs 97.
      const ageAtCollection =
        client && typeof client.ageAsOf === 'function'
          ? client.ageAsOf(collectionDate)
          : undefined;
      if (typeof ageAtCollection === 'number' && !isNaN(ageAtCollection)) {
        result.clientAge = Math.min(ageAtCollection, 90);
      }

      // Menstrual phase drives which FSH/LH/estradiol reference ranges apply
      // (e.g. follicular vs luteal), so surface it for AI interpretation of the
      // sex-hormone markers. Only include when meaningfully set (not "None").
      if (menstrualPhase && menstrualPhase !== 'None') {
        result.menstrualPhase = menstrualPhase;
      }

      // Filter notes
      if (
        bloodworkNotes &&
        bloodworkNotes.notes &&
        Array.isArray(bloodworkNotes.notes)
      ) {
        const filteredNotes = bloodworkNotes.notes.filter((note: any) => {
          // The Recommended Supplements note is not prose — it is the list of
          // products the practitioner took off the client's list, stored in the
          // notes array because that is where per-test state lives. Exporting
          // it would hand the model a bare list of product names labeled as a
          // practitioner note.
          if (note.section === Section.RECOMMENDED_SUPPLEMENTS) return false;
          // And the dose-type setting beside it: one word of state, not prose.
          if (note.section === CLIENT_LIST_DOSE_TYPE_SECTION) return false;
          // The practitioner's private half of the Insights card NEVER rides
          // in this raw blob. It has exactly one route into the export, the
          // top-level `notes` map, which buildAIExportData gates behind
          // includePractitionerInsights so the client-facing "Overall Progress
          // & Trends" draft cannot receive it. This replacer is static and
          // cannot see that option, so if the note were allowed through here
          // it would reach the draft whatever the caller asked for — which is
          // precisely how it leaked when the gate was added to the builder
          // alone (practitioner-insights-private.test.tsx).
          if (note.section === Section.PRACTITIONER_INSIGHTS) return false;
          return note.text && note.text.trim().length > 0;
        });

        // Only include if there are valid notes
        if (filteredNotes.length > 0) {
          result.bloodworkNotes = { notes: filteredNotes };
        }
      }

      return result;
    }

    // Handle Client
    if (value instanceof Client) {
      const {
        id,
        practitionerId,
        createDate,
        lastModifiedDate,
        firstName,
        lastName,
        alcoholDrinker,
        cigaretteSmoker,
        cigarettesSmoked,
        standardDrinks,
        // Client-level "Overall Practitioner Notes" is a per-test SNAPSHOT here and
        // can be stale; the export sources it from the LIVE client at the top level
        // instead, so strip it from the embedded client snapshot.
        overallPractitionerNotes,
        // The practitioner's private half of Overall Progress & Trends never
        // leaves the app at all. It is not sourced at top level either (unlike
        // the two above), because buildAIExportData also builds the
        // client-facing summary draft and this note is the one thing promised
        // never to reach the client.
        overallPractitionerPrivateNotes,
        // Same story: the comparison Notes column is emitted ONCE at top level
        // from the live client (AIExportBuilder's comparisonSectionNotes), so the
        // snapshot copy here was both a duplicate and the stale one.
        comparisonSectionNotes,
        // Date of birth is a direct identifier and the file is downloaded to be
        // handed to an outside AI. The Bloodwork branch emits `clientAge`
        // (age at the collection date) in its place, which is what the ranges
        // were already selected on, so no interpretation is lost.
        dateOfBirth,
        // Elevation only ever fed range SELECTION (PanelRange filters on it) and
        // every range in this file is already elevation-matched. Keeping it in
        // the export added nothing but a hint at where the client lives.
        elevation,
        ...rest
      } = value as any;

      const formatted: any = { ...rest };

      // Format habits
      formatted.alcoholConsumption = `${standardDrinks ?? 0}/week`;
      formatted.cigarettesSmoking = `${cigarettesSmoked ?? 0}/day`;

      return formatted;
    }

    // Handle PanelMarker
    if (value instanceof PanelMarker) {
      const { field, previousPanelMarker, attribute, range, ...rest } = value;
      const json: any = { ...rest };

      // Add units from field if available
      if (field?.units) {
        json.units = field.units;
      }

      // We want to include range but stripped (PanelRange handling below)
      if (range) {
        json.range = range;
      }

      if (typeof json.percentFromMean === 'number') {
        json.percentFromMean = MathUtils.round(json.percentFromMean, 2);
      }
      return json;
    }

    // Handle PanelRange
    if (value instanceof PanelRange) {
      const {
        id,
        gender,
        ethnicity,
        ageMin,
        ageMax,
        precision,
        mpvMin,
        mpvMax,
        elevationMin,
        elevationMax,
        ...rest
      } = value;
      return rest;
    }

    // Handle Analysis (base class for all specific analyses like WBCAnalysis, etc)
    // We check for 'ba' property which is the circular reference.
    if (value instanceof Analysis) {
      const { ba, possibleRootCauseCategories, ...rest } = value as any;
      return rest;
    }

    return value;
  }
}
